/**
 * Client-side fuzzy search engine for the command palette.
 *
 * Scores each candidate against the user query by checking label, description,
 * and keyword matches.  Multi-word queries require every word to match
 * somewhere in the candidate's combined text.  Results are sorted by
 * descending score, then alphabetically by label as a tiebreaker.
 */

import type { NavLinkItem } from "../../nav/navConfig.js";
import type { SearchMeta } from "./searchIndex.js";

export type SearchableItem = {
  item: NavLinkItem;
  sectionId: string;
  sectionLabel: string;
  groupLabel?: string;
  /** The command-palette category header: the OUTERMOST navigable ancestor
   *  group's id/label, so items under a navigable group head under that group
   *  (the three Dashboards — App, System, Streaming — head separately) instead
   *  of collapsing under the top-level nav section. Absent for direct section
   *  items and content anchors, which keep their sectionId/sectionLabel. */
  headerId?: string;
  headerLabel?: string;
};

export type SearchResult = SearchableItem & {
  description: string;
  score: number;
};

/** Maximum results returned from a single search. */
const MAX_RESULTS = 50;

/**
 * Score a single query word against one candidate.
 * Returns 0 when there is no match.
 */
function scoreWord(word: string, label: string, description: string, keywords: string[]): number {
  // Exact label match
  if (label === word) return 100;

  // Label starts with query word
  if (label.startsWith(word)) return 90;

  // Label word boundary match (any word in the label starts with the query word)
  const labelWords = label.split(/[\s\-_/]+/);
  if (labelWords.some((lw) => lw.startsWith(word))) return 80;

  // Label contains query word as substring
  if (label.includes(word)) return 70;

  // Description contains query word
  if (description.includes(word)) return 50;

  // Keyword starts with query word
  if (keywords.some((kw) => kw.startsWith(word))) return 40;

  // Keyword contains query word
  if (keywords.some((kw) => kw.includes(word))) return 30;

  return 0;
}

export function searchItems(
  query: string,
  items: SearchableItem[],
  metadata: Record<string, SearchMeta>
): SearchResult[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [];

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const results: SearchResult[] = [];

  for (const candidate of items) {
    const { item } = candidate;
    const meta = metadata[item.id];
    const label = item.label.toLowerCase();
    const description = (meta?.description ?? "").toLowerCase();
    const keywords = meta?.keywords?.map((k) => k.toLowerCase()) ?? [];

    if (words.length === 1) {
      const s = scoreWord(words[0]!, label, description, keywords);
      if (s > 0) {
        results.push({
          ...candidate,
          description: meta?.description ?? "",
          score: s
        });
      }
    } else {
      // Multi-word: every word must match somewhere
      let allMatch = true;
      let totalScore = 0;

      for (const word of words) {
        const s = scoreWord(word, label, description, keywords);
        if (s === 0) {
          allMatch = false;
          break;
        }
        totalScore += s;
      }

      if (allMatch) {
        results.push({
          ...candidate,
          description: meta?.description ?? "",
          // Average score across words preserves relative ranking
          score: totalScore / words.length
        });
      }
    }
  }

  // Sort: highest score first, then alphabetically by label
  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.item.label.localeCompare(b.item.label);
  });

  return results.slice(0, MAX_RESULTS);
}
